import WidgetInstance from "@src/components/widgets/WidgetInstance/WidgetInstance";
import { IWidgetGroup, IWidgetLayout } from "@src/interface/Widget";
import React, {
  createRef,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import "./WidgetLayout.styles.scss";
import { useLocation } from "@reach/router";
import clsx from "clsx";
import hasWindow from "@src/utils/hasWindow";
import { _WidgetLayoutRoot } from "@src/components/widgets/WidgetLayout/WidgetLayout.styled";
import HighlightedTypographyContext from "design-system/src/Components/HighlightedTypography/HighlightedTypography.context";
import ButtonContext from "design-system/src/Foundation/Button/Button.context";
import FlexBox from "design-system/src/Components/FlexBox/FlexBox";
import SuspenseAfterInteraction from "@src/lib/AfterInteraction/SuspenseAfterInteraction";
import TypographyContext from "design-system/src/Foundation/Typography/Typography.context";

export interface ILayoutListProps {
  children?: ReactNode;
  readonly list?: Array<Partial<IWidgetLayout> | null>;
  collectionPosition: number;
  group: Partial<IWidgetGroup>;
  loadIndexStart?: number;
  index?: number;
}

const firstLoad = 5;

const generateAnchorHash = (index: number, key: string = "cgv") =>
  `#${key}-${index}`;

const WidgetLayout = (props: ILayoutListProps) => {
  const {
    list: listIn = [],
    group: { titre_seo },
    loadIndexStart,
    index = 0,
  } = props;
  const { hash } = useLocation();

  const list = listIn.filter((l): l is Partial<IWidgetLayout> => l !== null);
  const hasBackgroundColor = list.some((l) => !!l?.background_color);
  const [backgroundColor, setBackgroundColor] = useState<string>(
    list[0]?.background_color || "transparent"
  );
  const layoutRefs: Array<React.RefObject<HTMLDivElement>> = useMemo(
    () => list.map(() => createRef()),
    [list]
  );

  useEffect(() => {
    if (hasBackgroundColor && hasWindow()) {
      const THRESHOLD_RATIO = 0.66;
      const handleScroll = (e: Event) => {
        const { innerHeight = 1 } = window;
        let mostVisibleRatio = 0;
        let bgColor;
        layoutRefs.forEach((ref, i) => {
          if (ref.current) {
            const boundingClientRect = ref.current.getBoundingClientRect();
            const { bottom, top } = boundingClientRect;
            const topMax = Math.max(0, Math.min(top, innerHeight));
            const bottomMax = Math.max(0, Math.min(bottom, innerHeight));
            const visibleHeight = bottomMax - topMax;
            const visibleRatio = visibleHeight / innerHeight;
            if (
              visibleRatio > THRESHOLD_RATIO &&
              visibleRatio >= mostVisibleRatio
            ) {
              mostVisibleRatio = visibleRatio;
              bgColor = list[i].background_color || "transparent";
            }
          }
        });
        bgColor && setBackgroundColor(bgColor);
      };
      window.addEventListener("scroll", handleScroll);
      return () => window.removeEventListener("scroll", handleScroll);
    }
  }, []);

  let instanceIndex = 0;

  return (
    <_WidgetLayoutRoot
      className={clsx("widget-layout-root", `widget-layout-root-${index}`)}
      style={{ backgroundColor: list.length > 1 ? undefined : backgroundColor }}
    >
      {list.map((layout, i) => {
        let {
          instance_list = [],
          display_title_top,
          pagination,
          display_title_bottom,
          element_per_layout,
          pagination_key,
          cta_background_color,
          cta_font_color,
          highlight_color,
          background_color,
          font_color,
        } = layout;

        const renderTitle = () => (
          <div className="title-bloc">
            {(display_title_top || pagination) && (
              <FlexBox
                as="div"
                align="center"
                justify="space-between"
                className="container"
              >
                {display_title_top && (
                  <h1 className="main-title">{titre_seo}</h1>
                )}
                {pagination && (
                  <FlexBox as="ul" className="anchor-list">
                    {(instance_list || []).map((_, i) => {
                      return (
                        <li key={i}>
                          <a
                            href={generateAnchorHash(
                              i + 1,
                              pagination_key || ""
                            )}
                            className={clsx({
                              active: hash
                                ? hash ===
                                  generateAnchorHash(
                                    i + 1,
                                    pagination_key || ""
                                  )
                                : i === 0,
                            })}
                          >
                            {i + 1}
                          </a>
                        </li>
                      );
                    })}
                  </FlexBox>
                )}
              </FlexBox>
            )}
          </div>
        );

        return (
          <TypographyContext.Provider
            key={i}
            value={{ color: font_color || undefined }}
          >
            <HighlightedTypographyContext.Provider
              key={i}
              value={{
                highlightedColor: highlight_color || undefined,
              }}
            >
              <ButtonContext.Provider
                value={{
                  textColor: cta_font_color || undefined,
                  backgroundColor: cta_background_color || undefined,
                }}
              >
                <div
                  ref={layoutRefs[i]}
                  style={{ backgroundColor: background_color || undefined }}
                  className={clsx("widget-layout", `widget-layout-${i}`)}
                >
                  {display_title_top && !!element_per_layout && renderTitle()}

                  {instance_list?.map((instance, index) => {
                    if (!instance) {
                      return null;
                    }
                    const { widget_class_name, template, ...instanceProps } =
                      instance;
                    const key = instance.id + "_" + index;
                    const loadIndex = loadIndexStart
                      ? loadIndexStart + index
                      : 0;

                    const canLoad = loadIndex < firstLoad;

                    const instanceRender = (
                      <WidgetInstance
                        className={`load-index-${loadIndex} widget-instance-${instanceIndex}`}
                        key={key}
                        layoutPosition={index + 1}
                        layoutList={list}
                        collectionPosition={index + 1}
                        componentName={instance.widget_class_name || ""}
                        template={instance.template || ""}
                        paginationKey={pagination_key || undefined}
                        widget_class_name={widget_class_name || ""}
                        {...instanceProps}
                      />
                    );
                    instanceIndex++;
                    if (!canLoad) {
                      return (
                        <SuspenseAfterInteraction key={key}>
                          {instanceRender}
                        </SuspenseAfterInteraction>
                      );
                    }
                    return instanceRender;
                  })}

                  {display_title_bottom &&
                    !!element_per_layout &&
                    renderTitle()}
                </div>
              </ButtonContext.Provider>
            </HighlightedTypographyContext.Provider>
          </TypographyContext.Provider>
        );
      })}
    </_WidgetLayoutRoot>
  );
};

export default WidgetLayout;
